define([
    '../Util/Check',
    '../Util/defaultValue',
    '../Util/defined'
], function (
    Check,
    defaultValue,
    defined) {
    'use strict'

    /**
     * 将单个十进制度数值转换为度分秒格式的字符串组件。
     * @private
     * @param {number} deg - 十进制度数值。
     * @param {'lat'|'lon'|'height'} type - 坐标类型（'lat'表示纬度，'lon'表示经度，'height'表示高度）。
     * @param {number} precision - 秒的小数位数。
     * @returns {string} 度分秒格式的字符串组件（例如："74° 0' 21.60\" W"）或错误消息。
     */
    function _convertSingleValueToDMS(deg, type, precision) {
        // Input validation (simplified assuming internal calls are mostly valid)
        if (typeof deg !== 'number' || isNaN(deg)) {
            return "Invalid Value"
        }
        // Allow 'angle' type for z-value or non-geographic angles
        if (type !== 'lat' && type !== 'lon' && type !== 'height') {
            return "Invalid Type"
        }

        // Determine direction character (N/S for lat, E/W for lon, none for angle)
        let direction = ''
        let isValidRange = true // Flag to check if value is within typical range

        if (type === 'lat') {
            if (deg < -90 || deg > 90) {
                isValidRange = false
                console.warn(`Latitude value ${deg} out of range [-90, 90].`)
                // Optionally return an error or proceed with conversion
                // return "Lat out of range"
            }
            direction = deg >= 0 ? 'N' : 'S'
        } else if (type === 'lon') {
            if (deg < -180 || deg > 180) {
                isValidRange = false
                console.warn(`Longitude value ${deg} out of range [-180, 180].`)
                // Optionally return an error or proceed with conversion
                // return "Lon out of range"
            }
            direction = deg >= 0 ? 'E' : 'W'
        }
        // No direction needed for 'angle' type

        // Use absolute value for D, M, S calculations
        let absDeg = Math.abs(deg)

        let degrees = Math.floor(absDeg)
        let minutesDecimal = (absDeg - degrees) * 60
        let minutes = Math.floor(minutesDecimal)
        // Calculate seconds and round *before* checking for 60
        let seconds = ((minutesDecimal - minutes) * 60)
        seconds = parseFloat(seconds.toFixed(precision)) // Apply precision early

        // Handle potential rounding issues causing seconds or minutes >= 60
        if (seconds >= 60) {
            seconds = 0 // Reset seconds
            minutes += 1 // Increment minutes
        }
        if (minutes >= 60) {
            minutes = 0 // Reset minutes
            degrees += 1 // Increment degrees
            // Note: This doesn't handle wrapping around 180 lon / 90 lat
            // If 179.9999 rounds up, degrees could become 181. For display, this might be ok.
        }

        // Format seconds to ensure correct number of decimal places
        const formattedSeconds = seconds.toFixed(precision)

        // Add a space before the direction if it exists
        const directionSuffix = direction ? ' ' + direction : ''

        return `${degrees}° ${minutes}' ${formattedSeconds}"${directionSuffix}`
    }
    /**
     * 三元向量对象，可表示三维坐标、RGB值颜色等。
     * @param {Number} x x坐标值。
     * @param {Number} y y坐标值。
     * @param {Number} z z坐标值。
     * 
     * @alias Vec3Obj
     * @constructor
     *
     */
    class Vec3Obj {
        /**
         * @description 在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "Vec3Obj"
        /**
         * @description x坐标值。
         * @type {Number}
         */
        x
        /**
         * @description y坐标值。
         * @type {Number}
         */
        y
        /**
         * @description z坐标值。
         * @type {Number}
         */
        z

        constructor(x, y, z) {
            this.x = defaultValue(x, 0.0)
            this.y = defaultValue(y, 0.0)
            this.z = defaultValue(z, 0.0)
        }

        /**
         * 将Vec3Obj实例转换成"x y z"格式的字符串。
         *
         * @returns {String} "x y z"格式的字符串。
         */
        toStr(split1, split2) {
            var firstSplit = defaultValue(split1, " ")
            var secondSplit = defaultValue(split2, "")
            var result = String(this.x) + firstSplit + String(this.y) + firstSplit + String(this.z) + secondSplit
            return result
        }

        /**
          * 将Vec3Obj实例的x (经度), y (纬度) 转换为度分秒(DMS)格式的字符串。
          * 可以选择是否转换z值以及如何显示z值。
          *
          * @param {object} [options] 配置选项。
          * @param {number} [options.precision=2] 秒的小数位数。
          * @param {boolean} [options.convertZ=false] 是否将z值也转换为DMS格式（作为通用角度，无方向）。
          * @param {string} [options.zLabel="Z"] z值的标签。
          * @param {string} [options.separator=", "] 各部分之间的分隔符。
          * @returns {String} 度分秒格式的字符串, 例如 "Lon: 12° 34' 56.78" E, Lat: 45° 12' 34.56" N, Z: 100.0"
          */
        toDMSStr(options) {
            const defaultOptions = {
                precision: 2,
                convertZ: false,
                zLabel: "Z",
                separator: ", "
            }
            const mergedOptions = { ...defaultOptions, ...options }

            // Validate precision
            let isValidPrecision = true
            if (typeof mergedOptions.precision !== 'number' ||  // 必须是数字
                !Number.isInteger(mergedOptions.precision) || // 必须是整数
                mergedOptions.precision < 0) {                 // 必须非负
                isValidPrecision = false
            }
            // 如果无效，使用默认值并发出警告
            if (!isValidPrecision) {
                console.warn(`Invalid precision value (${mergedOptions.precision}). Must be a non-negative integer. Defaulting to ${defaultOptions.precision}.`)
                mergedOptions.precision = defaultOptions.precision
            }


            const lonDMS = _convertSingleValueToDMS(this.x, 'lon', mergedOptions.precision)
            const latDMS = _convertSingleValueToDMS(this.y, 'lat', mergedOptions.precision)

            let zStr
            if (mergedOptions.convertZ) {
                // Convert Z as a generic angle (no N/S/E/W)
                zStr = `${mergedOptions.zLabel}: ${_convertSingleValueToDMS(this.z, 'angle', mergedOptions.precision)}`
            } else {
                // Represent Z as its raw value (maybe formatted)
                // Example: format z to the same precision as seconds for consistency
                zStr = `${mergedOptions.zLabel}: ${this.z.toFixed(mergedOptions.precision)}`
                // Or just use the raw value:
                // zStr = `${mergedOptions.zLabel}: ${this.z}`
            }

            // Construct the final string
            return `Lon: ${lonDMS}${mergedOptions.separator}Lat: ${latDMS}${mergedOptions.separator}${zStr}`
        }
        /**
         * 将"x y z"格式的字符串转换成Vec3Obj实例。
         *
         * @param {String} str "x y z"格式或者{"x":1,"y":2,"z":3}类型的字符串。
         * @param {Vec3Obj} [result] 如果未定义，将创建一个Vec3Obj新实例。
         * @returns {Vec3Obj} Vec3Obj实例。
         */
        static fromStr(str, result) {
            Check.typeOf.string('str', str)
            //>>includeEnd('debug')

            if (!defined(result)) {
                result = new Vec3Obj()
            }

            // 尝试解析为JSON
            if (str.includes('{') && str.includes('}')) {
                const obj = JSON.parse(str);
                if (defined(obj.x)) result.x = obj.x;
                if (defined(obj.y)) result.y = obj.y;
                if (defined(obj.z)) result.z = obj.z;
                return result;
            }
            var info = str.split(' ')
            result.x = parseFloat(info[0])
            result.y = parseFloat(info[1])
            result.z = parseFloat(info[2])
            return result
        }
        /**
         * 将一定规则格式的字符串转换成Vec3Obj实例。
         *
         * @param {String} str 一定规则格式的字符串。
         * @returns {Vec3Obj} Vec3Obj实例。
         */
        static fromStrEx(str) {
            // 移除字符串开头和结尾的空白
            str = str.trim()

            // 移除末尾的任何非数字字符（如分号、逗号等）
            str = str.replace(/[^\d.-]+$/, '')

            // 用任何非数字字符（除了负号和小数点）作为分隔符来分割字符串
            const numbers = str.split(/[^\d.-]+/).filter(Boolean).map(num => parseFloat(num))

            // 检查是否正好有三个数字
            if (numbers.length !== 3 || numbers.some(isNaN)) {
                throw new Error('Invalid str: Must be exactly three numbers')
            }

            // 返回三元数组
            return new Vec3Obj(numbers[0], numbers[1], numbers[2])
        }
    }
    return Vec3Obj
})